import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { clientApi, useClientAuth } from "@/context/ClientAuth";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const inputCls = "w-full bg-[#F9F6F1] border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors";

export const AccountGateway = ({ token, email, name, onDone, onSkip }) => {
  const { setUser } = useClientAuth();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [exists, setExists] = useState(false);

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/intake/" + token;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Please choose a password with at least 8 characters."); return; }
    setError(""); setExists(false); setBusy(true);
    try {
      const res = await clientApi.post(`/intake/${token}/create-account`, { password });
      localStorage.setItem("client_token", res.data.session_token);
      setUser(res.data.user);
      onDone();
    } catch (err) {
      if (err.response?.status === 409) setExists(true);
      setError(err.response?.data?.detail || "Could not create your account. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      data-testid="account-gateway"
      initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="min-h-screen bg-[#041C3E] text-white flex flex-col"
    >
      <header className="px-6 h-16 flex items-center justify-between max-w-6xl mx-auto w-full">
        <p className="font-head font-black text-lg">The Next Move<span className="text-[#D94833]"> by Rhellex</span></p>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white text-[#041C3E] p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">
            <CheckCircle2 size={14} /> Intake received
          </div>
          <h1 data-testid="account-gateway-headline" className="font-head text-3xl font-black mt-3 leading-tight">
            Your intake is complete! Create your account to save your assessment.
          </h1>
          <p className="text-sm text-[#3A4A63] mt-3 leading-relaxed">
            Set up your personalized Next Move Dashboard to access your Career Discovery results,
            track upcoming sessions, and monitor your progress.
          </p>

          <button
            type="button"
            data-testid="account-google-button"
            onClick={signInWithGoogle}
            className="mt-6 w-full flex items-center justify-center gap-3 border border-[#041C3E] px-6 py-3.5 font-semibold tracking-wide hover:bg-[#041C3E] hover:text-white transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-[#E5E7EB] flex-1" />
            <span className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">or with email</span>
            <div className="h-px bg-[#E5E7EB] flex-1" />
          </div>

          {error && <p data-testid="account-gateway-error" className="mb-4 text-sm text-[#D94833]">{error}</p>}
          {exists && (
            <p className="mb-4 text-sm text-[#3A4A63]">
              <Link data-testid="account-go-signin" to="/login" className="font-semibold text-[#D94833] underline underline-offset-2">
                Sign in to your existing account
              </Link>{" "}to view your results.
            </p>
          )}

          <form onSubmit={submit} className="space-y-3" data-testid="account-create-form">
            <div>
              <label className="block text-xs font-semibold text-[#3A4A63] mb-1">Email address</label>
              <input data-testid="account-email" type="email" value={email || ""} readOnly disabled
                className={`${inputCls} opacity-70 cursor-not-allowed`} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3A4A63] mb-1">Create password</label>
              <input data-testid="account-password" type="password" required minLength={8}
                placeholder="Password (min 8 characters)" className={inputCls}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <button data-testid="account-create-button" type="submit" disabled={busy}
              className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Creating account…" : "Create account"}
            </button>
          </form>

          <button type="button" data-testid="account-skip" onClick={onSkip}
            className="mt-5 text-sm text-[#9CA3AF] hover:text-[#041C3E] underline underline-offset-2 transition-colors">
            View my results without saving
          </button>
        </div>
      </div>
    </motion.div>
  );
};
